import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Modal,
  FlatList,
  Alert,
} from 'react-native';

interface CountryCode {
  name: string;
  code: string;
  dialCode: string;
}

const countryCodes: CountryCode[] = [
  { name: '中国', code: 'CN', dialCode: '+86' },
  { name: '美国', code: 'US', dialCode: '+1' },
  { name: '日本', code: 'JP', dialCode: '+81' },
  { name: '韩国', code: 'KR', dialCode: '+82' },
  { name: '英国', code: 'GB', dialCode: '+44' },
  { name: '德国', code: 'DE', dialCode: '+49' },
  { name: '法国', code: 'FR', dialCode: '+33' },
  { name: '加拿大', code: 'CA', dialCode: '+1' },
  { name: '澳大利亚', code: 'AU', dialCode: '+61' },
  { name: '新加坡', code: 'SG', dialCode: '+65' },
  { name: '香港', code: 'HK', dialCode: '+852' },
  { name: '台湾', code: 'TW', dialCode: '+886' },
  { name: '马来西亚', code: 'MY', dialCode: '+60' },
  { name: '泰国', code: 'TH', dialCode: '+66' },
  { name: '印度', code: 'IN', dialCode: '+91' },
  { name: '印尼', code: 'ID', dialCode: '+62' },
];

interface InternationalPhoneInputProps {
  value: string;
  onChangeText: (phone: string) => void;
  placeholder?: string;
  editable?: boolean;
}

const InternationalPhoneInput: React.FC<InternationalPhoneInputProps> = ({
  value,
  onChangeText,
  placeholder = '请输入手机号',
  editable = true,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(countryCodes[0]);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');

  // 当国家或手机号变化时，更新完整的电话号码
  const updateFullPhoneNumber = (country: CountryCode, phone: string) => {
    const fullPhone = phone ? `${country.dialCode}${phone}` : '';
    onChangeText(fullPhone);
  };

  const handleCountrySelect = (country: CountryCode) => {
    setSelectedCountry(country);
    setShowCountryPicker(false);
    updateFullPhoneNumber(country, phoneNumber);
  };

  const handlePhoneChange = (phone: string) => {
    // 只允许数字
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    setPhoneNumber(cleanPhone);
    updateFullPhoneNumber(selectedCountry, cleanPhone);
  };

  const validatePhoneNumber = (country: CountryCode, phone: string): boolean => {
    if (!phone) {return false;}

    // 基本长度验证（大多数国家手机号在7-15位之间）
    const length = phone.length;
    return length >= 7 && length <= 15;
  };

  const isValid = validatePhoneNumber(selectedCountry, phoneNumber);

  const renderCountryItem = ({ item }: { item: CountryCode }) => (
    <TouchableOpacity
      style={styles.countryItem}
      onPress={() => handleCountrySelect(item)}
    >
      <Text style={styles.countryName}>{item.name}</Text>
      <Text style={styles.countryDialCode}>{item.dialCode}</Text>
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <View style={styles.inputContainer}>
        <TouchableOpacity
          style={styles.countrySelector}
          onPress={() => editable && setShowCountryPicker(true)}
          disabled={!editable}
        >
          <Text style={[styles.dialCode, !editable && styles.disabled]}>
            {selectedCountry.dialCode}
          </Text>
          <Text style={[styles.arrow, !editable && styles.disabled]}>▼</Text>
        </TouchableOpacity>

        <TextInput
          style={[styles.phoneInput, !isValid && phoneNumber && styles.invalidInput]}
          placeholder={placeholder}
          value={phoneNumber}
          onChangeText={handlePhoneChange}
          keyboardType="numeric"
          maxLength={15}
          editable={editable}
        />
      </View>

      <Modal
        visible={showCountryPicker}
        animationType="slide"
        onRequestClose={() => setShowCountryPicker(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>选择国家/地区</Text>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setShowCountryPicker(false)}
            >
              <Text style={styles.closeButtonText}>关闭</Text>
            </TouchableOpacity>
          </View>

          <FlatList
            data={countryCodes}
            renderItem={renderCountryItem}
            keyExtractor={(item) => item.code}
            style={styles.countryList}
          />
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    backgroundColor: '#fff',
    height: 50,
  },
  countrySelector: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 15,
    borderRightWidth: 1,
    borderRightColor: '#eee',
    minWidth: 80,
  },
  dialCode: {
    fontSize: 16,
    color: '#333',
    fontWeight: '600',
    marginRight: 5,
  },
  arrow: {
    fontSize: 12,
    color: '#666',
  },
  disabled: {
    color: '#999',
  },
  phoneInput: {
    flex: 1,
    height: '100%',
    fontSize: 16,
    color: '#333',
    paddingHorizontal: 15,
  },
  invalidInput: {
    borderColor: '#ff6b6b',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#fff',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    backgroundColor: '#f8f9fa',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  closeButton: {
    padding: 5,
  },
  closeButtonText: {
    color: '#4285F4',
    fontSize: 16,
    fontWeight: '600',
  },
  countryList: {
    flex: 1,
  },
  countryItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  countryName: {
    fontSize: 16,
    color: '#333',
  },
  countryDialCode: {
    fontSize: 16,
    color: '#666',
    fontWeight: '600',
  },
});

export default InternationalPhoneInput;
